import type { TherapySession } from './attendance.js';
import { fail } from './errors.js';

export interface ScheduleInput {
  clientId: string;
  therapistId: string;
  date: string;          // 첫 회기 날짜 YYYY-MM-DD (한국 시간)
  startTime: string;     // HH:MM
  durationMin: number;
  program: string;
  voucherProgram?: string;
  repeatWeeks: number;   // 1 = 한 번, 4 = 4주 동안 매주 같은 요일·시간
}

export const SESSION_DURATIONS = [30, 40, 50, 60, 80, 90, 120] as const;

const DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
const overlaps = (aStart: number, aEnd: number, bStart: number, bEnd: number) => aStart < bEnd && bStart < aEnd;
const ms = (iso: string) => new Date(iso).getTime();

/** 한국 시간(+09:00) ISO 문자열 */
const kstIso = (date: string, time: string) => `${date}T${time}:00+09:00`;
const addDays = (date: string, days: number) => {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/** 반복 일정까지 포함해 만들어질 회기들의 시작·종료 시각. */
export function plannedSlots(input: ScheduleInput): Array<{ startAt: string; endAt: string }> {
  if (!DATE_RE.test(input.date)) fail('VALIDATION', '날짜 형식은 YYYY-MM-DD 입니다.');
  if (!TIME_RE.test(input.startTime)) fail('VALIDATION', '시작 시간 형식은 HH:MM 입니다.');
  if (!(SESSION_DURATIONS as readonly number[]).includes(input.durationMin)) fail('VALIDATION', '수업 시간을 목록에서 고르세요.');
  if (!Number.isInteger(input.repeatWeeks) || input.repeatWeeks < 1 || input.repeatWeeks > 12) fail('VALIDATION', '반복은 1~12주입니다.');
  const [h, m] = input.startTime.split(':').map(Number) as [number, number];
  if (h < 7 || h * 60 + m + input.durationMin > 22 * 60) fail('VALIDATION', '수업은 07:00~22:00 사이에만 등록할 수 있습니다.');
  const endMin = h * 60 + m + input.durationMin; // 22:00 이전이라 날짜를 넘지 않는다
  const endTime = `${String(Math.floor(endMin / 60)).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
  return Array.from({ length: input.repeatWeeks }, (_, i) => {
    const date = addDays(input.date, i * 7);
    return { startAt: kstIso(date, input.startTime), endAt: kstIso(date, endTime) };
  });
}

export interface ScheduleConflict { startAt: string; reason: 'THERAPIST_BUSY' | 'CLIENT_BUSY'; sessionId: string }

/**
 * 일정 생성. 같은 치료사·같은 이용자의 겹치는 회기(취소 제외)가 하나라도 있으면
 * 아무것도 만들지 않고 CONFLICT 로 겹치는 날짜를 모두 알려준다(부분 생성 없음).
 */
export function createSessions(
  centerId: string,
  input: ScheduleInput,
  existing: readonly TherapySession[],
  newId: () => string,
): TherapySession[] {
  const program = input.program.trim();
  if (!program || program.length > 40) fail('VALIDATION', '프로그램명은 1~40자로 입력하세요.');
  const voucherProgram = input.voucherProgram?.trim() || undefined;
  if (voucherProgram && voucherProgram.length > 60) fail('VALIDATION', '바우처 사업명이 너무 깁니다.');
  const slots = plannedSlots(input);
  const live = existing.filter((s) => s.status !== 'CANCELLED');
  const conflicts: ScheduleConflict[] = [];
  for (const slot of slots) {
    const a = ms(slot.startAt), b = ms(slot.endAt);
    for (const s of live) {
      if (!overlaps(a, b, ms(s.startAt), ms(s.endAt))) continue;
      if (s.therapistId === input.therapistId) conflicts.push({ startAt: slot.startAt, reason: 'THERAPIST_BUSY', sessionId: s.id });
      else if (s.clientId === input.clientId) conflicts.push({ startAt: slot.startAt, reason: 'CLIENT_BUSY', sessionId: s.id });
    }
  }
  if (conflicts.length) {
    const days = [...new Set(conflicts.map((c) => c.startAt.slice(5, 10).replace('-', '/')))].join(', ');
    fail('CONFLICT', `이미 겹치는 일정이 있습니다: ${days}. 시간을 바꾸거나 반복 횟수를 줄이세요.`, { conflicts });
  }
  return slots.map((slot) => ({
    id: newId(), centerId, clientId: input.clientId, therapistId: input.therapistId, program, voucherProgram,
    startAt: slot.startAt, endAt: slot.endAt, status: 'SCHEDULED', version: 1,
  }));
}

export interface MoveTarget { date: string; startTime: string; therapistId: string }

export interface SessionMove {
  sessionId: string;
  centerId: string;
  fromStartAt: string;
  toStartAt: string;
  fromTherapistId: string;
  toTherapistId: string;
  /** 수업 시간(길이) 조절일 때만 */
  fromEndAt?: string;
  toEndAt?: string;
  by: string;
  at: string;
}

/** 회기를 옮겨도 되는 상태인지(달력에서 끌기 허용 여부와 같은 규칙). */
export function movableReason(s: TherapySession): string | null {
  if (s.voucherPaidAt) return '바우처 결제가 끝난 회기는 옮길 수 없습니다.';
  if (s.status !== 'SCHEDULED') return '예정 상태의 회기만 옮길 수 있습니다. 완료·결석·취소된 회기는 출결을 먼저 되돌리세요.';
  return null;
}

/** 겹치는 회기 찾기. 자기 자신과 취소된 회기는 제외한다. */
export function findConflicts(
  s: Pick<TherapySession, 'id' | 'clientId'>,
  slot: { startAt: string; endAt: string; therapistId: string },
  existing: readonly TherapySession[],
): ScheduleConflict[] {
  const a = ms(slot.startAt), b = ms(slot.endAt);
  const out: ScheduleConflict[] = [];
  for (const x of existing) {
    if (x.id === s.id || x.status === 'CANCELLED' || !overlaps(a, b, ms(x.startAt), ms(x.endAt))) continue;
    if (x.therapistId === slot.therapistId) out.push({ startAt: x.startAt, reason: 'THERAPIST_BUSY', sessionId: x.id });
    else if (x.clientId === s.clientId) out.push({ startAt: x.startAt, reason: 'CLIENT_BUSY', sessionId: x.id });
  }
  return out;
}

/** 옮겼을 때의 시작·종료 시각. 수업 길이는 그대로 둔다. */
export function movedSlot(s: TherapySession, to: Pick<MoveTarget, 'date' | 'startTime'>): { startAt: string; endAt: string } {
  if (!DATE_RE.test(to.date)) fail('VALIDATION', '날짜 형식은 YYYY-MM-DD 입니다.');
  if (!TIME_RE.test(to.startTime)) fail('VALIDATION', '시작 시간 형식은 HH:MM 입니다.');
  const duration = Math.round((ms(s.endAt) - ms(s.startAt)) / 60_000);
  const [h, m] = to.startTime.split(':').map(Number) as [number, number];
  const endMin = h * 60 + m + duration;
  if (h < 7 || endMin > 22 * 60) fail('VALIDATION', '수업은 07:00~22:00 사이에만 둘 수 있습니다.');
  const endTime = `${String(Math.floor(endMin / 60)).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
  return { startAt: kstIso(to.date, to.startTime), endAt: kstIso(to.date, endTime) };
}

/**
 * 일정 옮기기(다른 시간·날짜·선생님). 예정 상태이고 바우처 결제 전인 회기만 가능.
 * 같은 선생님 또는 같은 이용자와 겹치면 CONFLICT, 지난 시각으로는 옮길 수 없다.
 * 처음 잡혔던 시각은 movedFrom 에 남겨 부정결제 점검 때 함께 본다.
 */
export function moveSession(
  s: TherapySession,
  to: MoveTarget,
  existing: readonly TherapySession[],
  actor: { userId: string },
  opts: { expectedVersion: number; now: string },
): { session: TherapySession; move: SessionMove } {
  if (opts.expectedVersion !== s.version) fail('CONFLICT', '다른 사용자가 먼저 수정했습니다. 새로고침 후 다시 시도하세요.');
  const locked = movableReason(s);
  if (locked) fail('LOCKED', locked);
  const slot = movedSlot(s, to);
  if (slot.startAt === s.startAt && to.therapistId === s.therapistId) fail('VALIDATION', '같은 자리로는 옮길 수 없습니다.');
  if (ms(slot.startAt) < ms(opts.now)) fail('VALIDATION', '지난 시각으로는 옮길 수 없습니다.');
  const conflicts = findConflicts(s, { ...slot, therapistId: to.therapistId }, existing);
  if (conflicts.length) {
    const c = conflicts[0]!;
    const hm = c.startAt.slice(11, 16);
    fail('CONFLICT', c.reason === 'THERAPIST_BUSY'
      ? `그 시간에는 선생님의 다른 수업(${hm})이 있습니다.`
      : `그 시간에는 이 아동의 다른 수업(${hm})이 있습니다.`, { conflicts });
  }
  return {
    session: { ...s, ...slot, therapistId: to.therapistId, version: s.version + 1, movedFrom: s.movedFrom ?? s.startAt, movedAt: opts.now },
    move: { sessionId: s.id, centerId: s.centerId, fromStartAt: s.startAt, toStartAt: slot.startAt, fromTherapistId: s.therapistId, toTherapistId: to.therapistId, by: actor.userId, at: opts.now },
  };
}

export const RESIZE_STEP_MIN = 10;
export const RESIZE_MIN_MIN = 10;
export const RESIZE_MAX_MIN = 240;

/**
 * 수업 시간 조절 검사(같은 날·같은 선생님에서 시작·종료 시각만 바꾼다). 문제가 없으면 null.
 * 화면(끌어서 조절 미리보기)과 서버가 같은 함수를 쓴다.
 */
export function resizeProblem(s: TherapySession, startTime: string, endTime: string, existing: readonly TherapySession[], nowIso: string): string | null {
  if (!TIME_RE.test(startTime) || !TIME_RE.test(endTime)) return '시작·종료 시간을 확인하세요.';
  const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3));
  const a = toMin(startTime), b = toMin(endTime), d = b - a;
  if (a < 7 * 60 || b > 22 * 60 || d < RESIZE_MIN_MIN || d > RESIZE_MAX_MIN || d % RESIZE_STEP_MIN || a % RESIZE_STEP_MIN)
    return `수업은 07:00~22:00 사이, ${RESIZE_STEP_MIN}분 단위로 ${RESIZE_MIN_MIN}~${RESIZE_MAX_MIN}분까지 조절할 수 있습니다.`;
  const locked = movableReason(s);
  if (locked) return locked.replace('옮길 수', '시간을 바꿀 수');
  const date = s.startAt.slice(0, 10);
  const startAt = kstIso(date, startTime), endAt = kstIso(date, endTime);
  if (ms(s.startAt) < ms(nowIso) || ms(startAt) < ms(nowIso)) return '이미 시작되었거나 지난 수업은 시간을 바꿀 수 없습니다.';
  if (startAt === s.startAt && endAt === s.endAt) return '바뀐 시간이 없습니다.';
  const c = findConflicts(s, { startAt, endAt, therapistId: s.therapistId }, existing)[0];
  if (c) return c.reason === 'THERAPIST_BUSY' ? `선생님의 다른 수업(${c.startAt.slice(11, 16)})과 겹칩니다.` : `이 아동의 다른 수업(${c.startAt.slice(11, 16)})과 겹칩니다.`;
  return null;
}

/** 수업 시간 조절. 처음 일정(movedFrom)과 이동 이력을 남긴다. */
export function resizeSession(
  s: TherapySession,
  to: { startTime: string; endTime: string },
  existing: readonly TherapySession[],
  actor: { userId: string },
  opts: { expectedVersion: number; now: string },
): { session: TherapySession; move: SessionMove } {
  if (opts.expectedVersion !== s.version) fail('CONFLICT', '다른 사용자가 먼저 수정했습니다. 새로고침 후 다시 시도하세요.');
  const problem = resizeProblem(s, to.startTime, to.endTime, existing, opts.now);
  if (problem) fail(movableReason(s) ? 'LOCKED' : /겹칩니다/.test(problem) ? 'CONFLICT' : 'VALIDATION', problem);
  const date = s.startAt.slice(0, 10);
  const startAt = kstIso(date, to.startTime), endAt = kstIso(date, to.endTime);
  return {
    session: { ...s, startAt, endAt, version: s.version + 1, movedFrom: s.movedFrom ?? s.startAt, movedAt: opts.now },
    move: { sessionId: s.id, centerId: s.centerId, fromStartAt: s.startAt, toStartAt: startAt, fromEndAt: s.endAt, toEndAt: endAt, fromTherapistId: s.therapistId, toTherapistId: s.therapistId, by: actor.userId, at: opts.now },
  };
}

/* ---------------- 종일 센터 일정(휴무·결근·회의·워크숍) ---------------- */

export const CENTER_EVENT_KINDS = ['STAFF_OFF', 'MEETING', 'CENTER'] as const;
export type CenterEventKind = (typeof CENTER_EVENT_KINDS)[number];

/** 달력 맨 위 "종일" 줄에 보이는 일정. staffId 가 있으면 그 선생님 일정(예: 출근X·개인사정 결근). */
export interface CenterEvent {
  id: string;
  centerId: string;
  date: string; // YYYY-MM-DD
  title: string;
  kind: CenterEventKind;
  staffId?: string;
  createdBy: string;
  createdAt: string;
  cancelled?: boolean;
  version: number;
}

export function newCenterEvent(i: { id: string; centerId: string; date: string; title: string; kind: CenterEventKind; staffId?: string; by: string; now: string }): CenterEvent {
  if (!DATE_RE.test(i.date)) fail('VALIDATION', '날짜 형식은 YYYY-MM-DD 입니다.');
  const title = i.title.trim();
  if (title.length < 1 || title.length > 40) fail('VALIDATION', '일정 이름은 1~40자로 입력하세요.');
  if (!(CENTER_EVENT_KINDS as readonly string[]).includes(i.kind)) fail('VALIDATION', '일정 구분을 고르세요.');
  if (i.kind === 'STAFF_OFF' && !i.staffId) fail('VALIDATION', '휴무·결근은 선생님을 고르세요.');
  return { id: i.id, centerId: i.centerId, date: i.date, title, kind: i.kind, staffId: i.staffId || undefined, createdBy: i.by, createdAt: i.now, version: 1 };
}

/**
 * 2026년 공휴일(대체공휴일 포함). 달력 표시·데모 데이터용.
 * 운영에서는 한국천문연구원 특일 정보 API 등으로 매년 갱신해야 한다.
 */
export const KR_HOLIDAYS: Record<string, string> = {
  '2026-01-01': '신정', '2026-02-16': '설날 연휴', '2026-02-17': '설날', '2026-02-18': '설날 연휴', '2026-03-01': '삼일절', '2026-03-02': '대체공휴일',
  '2026-05-05': '어린이날', '2026-05-24': '부처님오신날', '2026-05-25': '대체공휴일', '2026-06-03': '지방선거', '2026-06-06': '현충일',
  '2026-08-15': '광복절', '2026-08-17': '대체공휴일', '2026-09-24': '추석 연휴', '2026-09-25': '추석', '2026-09-26': '추석 연휴',
  '2026-10-03': '개천절', '2026-10-05': '대체공휴일', '2026-10-09': '한글날', '2026-12-25': '성탄절',
};
